import { useEffect, useState } from "react";
import { publicRequest } from "../api/requestMethods";
import { COUNTRY } from "../config/market";

/**
 * useMinimumOrderValue: this market's live minimum order value, the smallest
 * basket of GOODS a rider is sent out for, per vendor.
 *
 * The figure is set in the dashboard (Settings > Minimum order value) and the
 * server enforces it at order-create with BELOW_MINIMUM_ORDER_VALUE. The carts
 * used to carry their own copy (30,000 in Uganda, 200 in Zambia), so lowering
 * it in Settings changed what the server accepted but not what the cart said:
 * a basket the server would take was still blocked on screen. This hook reads
 * the server's figure instead, so there is one number and the dashboard owns it.
 *
 * - Read from GET /api/minimum-order-value/public (no sign-in needed), once
 *   for every cart and checkout screen together, and again only after
 *   FRESH_FOR_MS (the server caches the value for the same 30 seconds).
 * - The last value read is kept in localStorage, so a returning shopper's cart
 *   answers at once instead of waiting for the request.
 * - Until a value has been read, and if every read fails, the result is 0:
 *   no notice and nothing blocked on screen. The server still refuses a short
 *   basket, so a missing read can only cost the early warning, never let a
 *   short order through and never block one the server would accept.
 *
 * Nothing here runs on page load: the first cart or checkout screen to mount
 * starts the read.
 */

const FRESH_FOR_MS = 30 * 1000;
const STORAGE_KEY = `lusuku:minimumOrderValue:${COUNTRY}`;

const toAmount = (raw) => {
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const readRemembered = () => {
  try {
    return toAmount(window.localStorage.getItem(STORAGE_KEY));
  } catch (err) {
    return null;
  }
};

const remember = (amount) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(amount));
  } catch (err) {
    /* Private window or blocked storage: the in-memory value still serves. */
  }
};

let current = readRemembered();
let readAt = 0;
let inFlight = null;
const listeners = new Set();

const publish = (amount) => {
  current = amount;
  listeners.forEach((listener) => listener(amount));
};

/** The floor as last read, or 0 while nothing has been read. */
export const currentMinimumOrderValue = () => current ?? 0;

/**
 * Read the floor from the server unless a read is fresh or already running.
 * Resolves to the floor in use afterwards (the previous one if the read failed).
 */
export const loadMinimumOrderValue = () => {
  if (Date.now() - readAt < FRESH_FOR_MS) return Promise.resolve(currentMinimumOrderValue());
  if (!inFlight) {
    /* Started inside a promise so that anything going wrong, even before a
       request exists, lands in the catch below instead of in a cart's render. */
    inFlight = Promise.resolve()
      .then(() => publicRequest.get("/api/minimum-order-value/public"))
      .then((res) => {
        const amount = toAmount(res?.data?.minimumOrderValue);
        if (amount !== null) {
          readAt = Date.now();
          remember(amount);
          publish(amount);
        }
        return currentMinimumOrderValue();
      })
      .catch(() => currentMinimumOrderValue())
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

export default function useMinimumOrderValue() {
  const [amount, setAmount] = useState(currentMinimumOrderValue);

  useEffect(() => {
    const listener = (next) => setAmount(next ?? 0);
    listeners.add(listener);
    /* A value may have landed between the first render and this effect. */
    setAmount(currentMinimumOrderValue());
    loadMinimumOrderValue();
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return amount;
}

/* Tests only: forget everything this module has read. */
export const resetMinimumOrderValueForTests = () => {
  current = readRemembered();
  readAt = 0;
  inFlight = null;
  listeners.clear();
};
